import type { RealtimeChannel, SupabaseClient } from '@supabase/supabase-js'
import {
  type ChannelDefinition,
  type EventName,
  type PayloadOf,
  type ChannelParamsOf,
  getEventSchema,
  resolveChannel,
} from './realtime-channel.types'

type EventHandler<T = unknown> = (payload: T) => void
type BroadcastMessage = {
  type: 'broadcast'
  event: string
  payload?: unknown
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function createSubscriber<T extends ChannelDefinition<any, any>>(
  channel: RealtimeChannel,
  channelName: string,
  channelDef: T,
  loggerFn?: (message: string) => void,
) {
  const handlers = new Map<string, EventHandler<PayloadOf<T, EventName<T>>>[]>()
  let isSubscribed = false
  function unsubscribe(event: string, handler: EventHandler<PayloadOf<T, EventName<T>>>) {
    const eventHandlers = handlers.get(event)
    if (!eventHandlers) {
      return
    }
    const index = eventHandlers.indexOf(handler as EventHandler)
    if (index > -1) {
      eventHandlers.splice(index, 1)
    }

    loggerFn?.(`Subscriber: Unsubscribing from event ${String(event)} on channel ${channelName}`)
    // If no more handlers for this event, remove it
    if (eventHandlers.length === 0) {
      handlers.delete(event)
    }

    // If no more handlers for this channel, remove the channel
    if (handlers.size === 0) {
      channel.unsubscribe()
      loggerFn?.(`Subscriber: Removed channel ${channelName}`)
      isSubscribed = false
    }
  }

  return {
    subscribe<E extends EventName<T>>(event: E, handler: EventHandler<PayloadOf<T, E>>) {
      const eventName = event as string

      loggerFn?.(`Subscriber: Adding handler for event ${eventName} on channel ${channelName}`)
      // Set up event listener for this specific event if it's the first handler
      if (!handlers.has(eventName)) {
        handlers.set(eventName, [])

        // Set up the broadcast listener for this specific event
        channel.on('broadcast', { event: eventName }, (msg: { payload: unknown }) => {
          loggerFn?.(
            `Subscriber: Received event ${eventName} on channel ${channelName} with payload ${JSON.stringify(msg.payload)}`,
          )
          // Find handlers for this event
          const eventHandlers = handlers.get(eventName)
          loggerFn?.(
            `Subscriber: Found ${eventHandlers?.length} handlers for event ${eventName} on channel ${channelName}`,
          )
          if (eventHandlers) {
            // Runtime validation on the client too (defense in depth)
            const schema = getEventSchema(channelDef, event)

            // Handle the case where Supabase sends {} for undefined payloads
            let payloadToValidate = msg.payload
            if (msg.payload && typeof msg.payload === 'object' && Object.keys(msg.payload).length === 0) {
              // If it's an empty object, convert to undefined for z.undefined() schema
              payloadToValidate = undefined
            }

            const parsed = schema.parse(payloadToValidate)
            // Call all handlers for this event
            eventHandlers.forEach((h) => h(parsed))
          }
        })
        // Subscribe once per channel
        if (!isSubscribed) {
          channel.subscribe()
          isSubscribed = true
        }
      }

      handlers.get(eventName)!.push(handler)

      // Return unsubscribe function
      return () => unsubscribe(eventName, handler)
    },
  }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function createPublisher<T extends ChannelDefinition<any, any>>(
  channel: RealtimeChannel,
  channelName: string,
  channelDef: T,
  loggerFn?: (message: string) => void,
) {
  return {
    async publish<E extends EventName<T>>(event: E, payload: PayloadOf<T, E>) {
      const schema = getEventSchema(channelDef, event)
      const parsed = schema.parse(payload)

      const message: BroadcastMessage = {
        type: 'broadcast',
        event: event as string,
        // If the payload is undefined, send an empty object as Supabase expects a payload
        payload: parsed !== undefined ? parsed : {},
      }
      loggerFn?.(`Publishing event ${String(event)} to channel ${channelName} with payload ${JSON.stringify(message)}`)
      const response = await channel.send(message)
      if (response === 'error') throw new Error('Failed to publish event')
    },
  }
}

function ensureConnection(supabase: SupabaseClient, loggerFn?: (message: string) => void) {
  // Check if we're in a browser environment
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return {
      cleanup: () => {},
    }
  }

  // Set up Supabase connection state callbacks.
  // Entries are [ref, callback] tuples in @supabase/realtime-js; the ref lets us
  // find and remove our own callbacks on cleanup.
  const stateChangeCallbacks = supabase.realtime.stateChangeCallbacks
  const OPEN_REF = 'forgd-ensure-connection-open'
  const CLOSE_REF = 'forgd-ensure-connection-close'
  const ERROR_REF = 'forgd-ensure-connection-error'

  // Connection opened callback
  const onOpen = () => loggerFn?.('🟢 Realtime connection opened')
  stateChangeCallbacks.open.push([OPEN_REF, onOpen])

  // Connection closed callback
  const onClose = () => loggerFn?.('🔴 Realtime connection closed')
  stateChangeCallbacks.close.push([CLOSE_REF, onClose])

  // Connection error callback
  const onError = (error: unknown) => {
    const errorMessage = error instanceof Error ? error.message : String(error)
    loggerFn?.(`❌ Realtime connection error: ${JSON.stringify(errorMessage)}`)
  }
  stateChangeCallbacks.error.push([ERROR_REF, onError])

  // Force reconnection if connection is not active
  const checkConnection = () => {
    if (!supabase.realtime.isConnected()) {
      loggerFn?.('⚠️ Connection lost, attempting to reconnect...')
      supabase.realtime.connect()
      return
    }
    loggerFn?.('✅ Supabase realtime connection is active')
  }

  // Check connection status periodically (every 60 seconds)
  const connectionCheckInterval = setInterval(checkConnection, 60000)

  // Also check on visibility change (when user comes back to tab)
  const handleVisibilityChange = () => {
    if (!document.hidden) {
      loggerFn?.('👁️ Tab became visible, checking connection...')
      checkConnection()
    }
  }

  document.addEventListener('visibilitychange', handleVisibilityChange)

  // Cleanup function
  const cleanup = () => {
    clearInterval(connectionCheckInterval)
    document.removeEventListener('visibilitychange', handleVisibilityChange)
    // Remove registered callbacks (match on the ref we registered with)
    stateChangeCallbacks.open = stateChangeCallbacks.open.filter(([ref]) => ref !== OPEN_REF)
    stateChangeCallbacks.close = stateChangeCallbacks.close.filter(([ref]) => ref !== CLOSE_REF)
    stateChangeCallbacks.error = stateChangeCallbacks.error.filter(([ref]) => ref !== ERROR_REF)
  }

  return {
    cleanup,
  }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function createChannel<T extends ChannelDefinition<any, any>>(
  supabase: SupabaseClient,
  channelDef: T,
  params?: ChannelParamsOf<T>,
  loggerFn?: (message: string) => void,
) {
  const channelName = resolveChannel(channelDef, params)
  const channel = supabase.channel(channelName)
  return {
    ...createSubscriber(channel, channelName, channelDef, loggerFn),
    ...createPublisher(channel, channelName, channelDef, loggerFn),
    ensureConnection: ensureConnection(supabase, loggerFn),
  }
}
